<template>
  <div class="product-selection-grid">PRODUCT SELECTION</div>
</template>

<script>
  import { ref, computed, onMounted, watch } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@shared/components/LoadingSpinner.vue';

  export default {
    name: 'ProductSelectionGrid',

    components: {
      BaseIcon,
      LoadingSpinner,
    },

    props: {
      // Array van product IDs die beschikbaar zijn
      availableProducts: {
        type: Array,
        required: true,
      },
      // Single of multiple selection
      selectionMode: {
        type: String,
        default: 'single',
        validator: (value) => ['single', 'multiple'].includes(value),
      },
      // Design opties
      showPricing: {
        type: Boolean,
        default: true,
      },
      showImages: {
        type: Boolean,
        default: true,
      },
      // Pre-selected product
      modelValue: {
        type: [String, Array],
        default: null,
      },
    },

    emits: ['update:modelValue', 'product-selected', 'selection-confirmed'],

    setup(props, { emit }) {
      // Service initialization moved to top

      return {};
    },
  };
</script>

<style scoped lang="scss">
  .product-selection-grid {
    display: flex;
    flex-direction: column;
    gap: 2rem;
  }

  .category-tabs {
    display: flex;
    gap: 0.5rem;
    border-bottom: 1px solid var(--slate-200);
    overflow-x: auto;
    padding-bottom: 1rem;

    .category-tab {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      border: none;
      background: none;
      color: var(--slate-600);
      font-weight: 500;
      border-radius: 6px;
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: nowrap;

      &:hover {
        background: var(--slate-100);
        color: var(--slate-800);
      }

      &--active {
        background: var(--brand-100);
        color: var(--brand-700);
      }

      .category-count {
        font-size: 0.875rem;
        color: var(--slate-500);
      }
    }
  }

  .loading-state {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 3rem;
    color: var(--slate-600);
  }

  .empty-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 4rem 2rem;
    text-align: center;
    color: var(--slate-500);

    svg {
      color: var(--slate-300);
      margin-bottom: 1rem;
    }

    h3 {
      margin: 0 0 0.5rem 0;
      color: var(--slate-700);
      font-size: 1.25rem;
    }

    p {
      margin: 0;
      font-size: 0.875rem;
    }
  }

  .product-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 1.5rem;
  }

  .product-card {
    border: 2px solid var(--slate-200);
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;
    background: white;
    position: relative;

    &--selectable {
      cursor: pointer;

      &:hover {
        border-color: var(--brand-300);
        box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
        transform: translateY(-2px);
      }
    }

    &--selected {
      border-color: var(--brand-500);
      box-shadow: 0 0 0 3px var(--brand-100);
    }

    &__image {
      position: relative;
      width: 100%;
      height: 200px;
      background: var(--slate-100);
      display: flex;
      align-items: center;
      justify-content: center;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }

    &__placeholder {
      color: var(--slate-400);
    }

    &__selected-indicator {
      position: absolute;
      top: 0.75rem;
      right: 0.75rem;
      width: 28px;
      height: 28px;
      background: var(--brand-500);
      color: white;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }

    &__content {
      padding: 1.5rem;
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__title {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      color: var(--slate-800);
      line-height: 1.3;
    }

    &__description {
      margin: 0;
      color: var(--slate-600);
      font-size: 0.875rem;
      line-height: 1.5;
      display: -webkit-box;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    &__usps {
      list-style: none;
      margin: 0;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;

      .usp-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        font-size: 0.875rem;
        color: var(--slate-700);

        svg {
          color: var(--green-500);
          flex-shrink: 0;
        }
      }
    }

    &__meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-top: auto;
      padding-top: 1rem;
      border-top: 1px solid var(--slate-100);

      .product-price {
        font-weight: 600;
        color: var(--brand-600);
        font-size: 1.125rem;
      }

      .product-options {
        font-size: 0.875rem;
        color: var(--slate-500);
      }
    }

    &__action {
      padding: 1rem 1.5rem;
      border-top: 1px solid var(--slate-100);
      background: var(--slate-50);
    }
  }

  .product-select-btn {
    width: 100%;
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-weight: 500;
    font-size: 0.875rem;
    cursor: pointer;
    transition: all 0.2s ease;
    border: 1px solid;

    &--default {
      background: white;
      color: var(--slate-700);
      border-color: var(--slate-300);

      &:hover {
        background: var(--slate-50);
        border-color: var(--slate-400);
      }
    }

    &--selected {
      background: var(--brand-500);
      color: white;
      border-color: var(--brand-500);

      &:hover {
        background: var(--brand-600);
        border-color: var(--brand-600);
      }
    }
  }

  .confirm-selection-btn {
    padding: 0.75rem 1.5rem;
    background: var(--brand-500);
    color: white;
    border: none;
    border-radius: 6px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background: var(--brand-600);
    }
  }

  .selection-summary {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    background: var(--brand-50);
    border: 1px solid var(--brand-200);
    border-radius: 8px;
    margin-top: 1rem;

    &__content {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      color: var(--brand-700);

      svg {
        color: var(--brand-500);
      }
    }
  }

  @media (max-width: 768px) {
    .product-grid {
      grid-template-columns: 1fr;
    }

    .selection-summary {
      flex-direction: column;
      gap: 1rem;
      text-align: center;
    }
  }
</style>
